import { Fragment, useState, type ReactNode } from 'react'
import { Check, X } from 'lucide-react'
import NodeCells, { effectiveTarget } from './NodeCells'
import { Breadcrumb, ParentSummary } from './PlanNodesPanelParts'
import { type RequestComment } from './revisionRequest'
import RowNotes from './RowNotes'

export interface PlanNode {
  id: string
  name: string
  shortName?: string | null
  revenue: string
  share: number
  growthPct?: number | null
  targetRevenue?: number | null
  special?: boolean
  isNewProduct?: boolean
  productNodeId?: string | null
  productNodeHasChildren?: boolean
  // Actual sales from the plan start through the day before the plan
  // period. Present only on a quarterly-revision version.
  actualRevenue?: string
  children: PlanNode[]
}

export interface BreadcrumbCrumb {
  id: string | null
  label: string
}

interface Props {
  nodes: PlanNode[]
  drillNodeId: string | null
  parentTarget: number
  // Denominator of Target Contribution: the plan total, plus the months
  // already sold on a quarterly revision (full-year share).
  topLevelTotal: number
  currency?: string | null
  breadcrumb: BreadcrumbCrumb[]
  /** The plan has been published — every node renders read-only, whatever its depth. */
  locked?: boolean
  // Open revision-request comments, keyed by draft node id.
  rowNotes?: Record<string, RequestComment[]>
  // Nodes the published plan already has; a quarterly revision offers no
  // Remove on them (set the target to 0 instead).
  publishedNodeIds?: Set<string>
  // Set only for a quarterly revision. Adds a "Q1-Q3 Actuals" column (the
  // months already sold) after Past Contribution; the target column holds
  // the remaining months only ("Q4 Target", with the full year under it) and
  // Growth reads on the full year.
  revision?: { quarters: string; actualQuarters: string } | null
  onRemove: (id: string) => void
  onDrill: (node: PlanNode) => void
  onNavigate: (id: string | null) => void
  onTargetChange: (id: string, target: number | null, growthPctOverride?: number | null) => void
}

// Target Contribution gets 120px so its two-line header fits inside its track.
const GRID_TEMPLATE = 'minmax(160px, 1fr) 100px 140px 190px 120px 90px'

function findNode(nodes: PlanNode[], id: string): PlanNode | null {
  for (const n of nodes) {
    if (n.id === id) return n
    const c = findNode(n.children, id)
    if (c) return c
  }
  return null
}

export default function PlanNodesPanel({
  nodes,
  drillNodeId,
  parentTarget,
  topLevelTotal,
  currency,
  breadcrumb,
  locked = false,
  rowNotes,
  revision,
  publishedNodeIds,
  onRemove,
  onDrill,
  onNavigate,
  onTargetChange
}: Props) {
  const drillNode = drillNodeId ? findNode(nodes, drillNodeId) : null
  const visibleChildren = drillNode ? drillNode.children : nodes
  const [showDescendants, setShowDescendants] = useState(false)
  // A revision adds the actuals track after Past Contribution.
  const gridTemplate = revision ? 'minmax(160px, 1fr) 100px 120px 140px 190px 120px 90px' : GRID_TEMPLATE

  const totalAllocated = visibleChildren.reduce((sum, n) => sum + effectiveTarget(n), 0)
  const remaining = parentTarget - totalAllocated
  const hasSpecial = visibleChildren.some((n) => n.special)

  type Row = { node: PlanNode; depth: number }
  const rows: Row[] = []
  const pushRows = (list: PlanNode[], depth: number) => {
    for (const n of list) {
      rows.push({ node: n, depth })
      if (showDescendants) pushRows(n.children, depth + 1)
    }
  }
  pushRows(visibleChildren, 0)

  return (
    <div
      className="border rounded-lg flex flex-col"
      style={{ borderColor: 'var(--border)', background: 'var(--background)' }}
    >
      <div
        className="p-4 border-b flex items-center justify-between gap-3"
        style={{ borderColor: 'var(--border)' }}
      >
        <Breadcrumb crumbs={breadcrumb} onNavigate={onNavigate} />
        <button
          type="button"
          onClick={() => setShowDescendants((v) => !v)}
          className="text-xs hover:underline"
          style={{ color: 'var(--link)' }}
        >
          {showDescendants ? 'Hide child nodes' : 'Display child nodes'}
        </button>
      </div>

      {drillNode && (
        <ParentSummary
          parentTarget={parentTarget}
          childrenSum={totalAllocated}
          remaining={remaining}
          currency={currency}
          hasSpecial={hasSpecial}
        />
      )}

      {rows.length > 0 ? (
        <div className="grid" style={{ gridTemplateColumns: gridTemplate }}>
          <HeaderCell>Node</HeaderCell>
          <HeaderCell align="right">Past Contribution</HeaderCell>
          {revision && <HeaderCell align="right">{revision.actualQuarters} Actuals</HeaderCell>}
          <HeaderCell align="right"><span className="whitespace-nowrap">Growth</span></HeaderCell>
          <HeaderCell align="right" paddingRight={34}>{revision ? `${revision.quarters} Target` : 'Target Revenue'}</HeaderCell>
          <HeaderCell align="right">Target Contribution</HeaderCell>
          <HeaderCell align="center">Action</HeaderCell>

          {rows.map(({ node, depth }) => {
            const target = effectiveTarget(node)
            // Target Contribution reads on the full year: on a revision the
            // months already sold count on both sides (0 on a plain plan).
            const newShare = topLevelTotal > 0 ? (target + Number(node.actualRevenue ?? 0)) / topLevelTotal : 0
            const readOnly = locked || depth > 0
            const fillableRest = depth === 0 && drillNode ? Math.max(remaining, 0) : 0
            const deductibleRest = depth === 0 && drillNode ? Math.max(-remaining, 0) : 0
            // Every plan version always keeps a top-level "Other" and a
            // "New Products" node (the pricing upload needs somewhere to put
            // products the tree does not name), so those two cannot be removed.
            // An "Other" node inside a drilled-into node is still optional.
            const removable = !node.isNewProduct
              && !(node.special && drillNodeId == null && depth === 0)
              && !publishedNodeIds?.has(node.id)
            const notes = rowNotes?.[node.id]
            return (
              <Fragment key={node.id}>
                <NodeCells
                  node={node}
                  depth={depth}
                  removable={removable}
                  readOnly={readOnly}
                  newShare={newShare}
                  currency={currency}
                  fillableRest={fillableRest}
                  deductibleRest={deductibleRest}
                  highlighted={Boolean(notes?.length)}
                  revision={revision}
                  onDrill={onDrill}
                  onRemove={onRemove}
                  onTargetChange={onTargetChange}
                />
                {notes && notes.length > 0 && (
                  <div style={{ gridColumn: '1 / -1' }} className="border-b">
                    <RowNotes notes={notes} withTabBar={false} />
                  </div>
                )}
              </Fragment>
            )
          })}
        </div>
      ) : (
        <div className="p-4 text-sm" style={{ color: 'var(--muted-foreground)' }}>
          No child nodes yet.
        </div>
      )}

      <div
        className="px-4 py-2 border-t text-xs flex items-center gap-4"
        style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}
      >
        <div className="flex items-center gap-3 ml-auto">
          <span className="flex items-center gap-1">
            <Check size={12} strokeWidth={2.5} style={{ color: 'var(--success, #16a34a)' }} aria-hidden />
            Children sum matches parent
          </span>
          <span className="flex items-center gap-1">
            <X size={12} strokeWidth={2.5} style={{ color: 'var(--destructive, #c53030)' }} aria-hidden />
            Children sum doesn't match parent
          </span>
        </div>
      </div>
    </div>
  )
}

function HeaderCell({
  children,
  align = 'left',
  paddingRight
}: {
  children: ReactNode
  align?: 'left' | 'right' | 'center'
  paddingRight?: number
}) {
  const justify = align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start'
  return (
    <div
      className="px-3 py-1.5 border-b text-[11px] uppercase tracking-wide font-medium flex items-center"
      style={{
        borderColor: 'var(--border)',
        color: 'var(--muted-foreground)',
        justifyContent: justify,
        textAlign: align,
        paddingRight: paddingRight ?? undefined
      }}
    >
      {children}
    </div>
  )
}
